import React, { useEffect, useState, useCallback, useRef } from "react";
import SurveyListHeader from "../../../../../component/common/layout/SurveyListHeader";
import SurveyListSidebar from "../../../../../component/common/layout/SurveyListSidebar";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import useVerifySurveyId from "../../../../../hooks/verifySurveyId";
import { getPrivacyPolicy, savePrivacyPolicy } from "../../../../../store/actions/surveyAction";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import LinkExtension from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import { useTranslation } from "react-i18next";
export default function PrivacyPolicyNew() {
  const survey_id = useVerifySurveyId();
  const { survey_name } = useSelector((state) => state.permissionsData);
  const dispatch = useDispatch();
  const { privacyPolicy, privacyPolicyLoading } = useSelector((state) => state.surveyData);
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();
  const colorInputRef = useRef(null);
  const [currentColor, setCurrentColor] = useState('#000000');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        bulletList: true,
        orderedList: true,
      }),
      Underline,
      LinkExtension.configure({ openOnClick: true, autolink: true, linkOnPaste: true }),
      TextStyle,
      Color.configure({ types: ['textStyle'] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: "",
    autofocus: false,
    editable: window.modulePermissions.M19.edit == 1,
  });

  // Load initial HTML from API using Redux
  useEffect(() => {
    if (survey_id) {
      dispatch(getPrivacyPolicy(survey_id));
    }
  }, [survey_id, dispatch]);

  // When policy data arrives, set editor content
  useEffect(() => {
    if (editor && privacyPolicy && privacyPolicy.privacy_content) {
      editor.commands.setContent(privacyPolicy.privacy_content);
    }
  }, [editor, privacyPolicy]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl || 'https://');
    if (url === null) return; // cancelled
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const onSave = async () => {
    if (!editor || !survey_id) return;
    setSaving(true);
    const html = editor.getHTML();
    dispatch(savePrivacyPolicy(survey_id, html, () => setSaving(false)));
  };

  return (
    <div className="d-flex flex-column main-outer-wrapper main-site-wrapper bg-white">
      <SurveyListHeader />
      <div id="viewport" className="flex-grow-1 d-flex survey-main-wrapper bg-white">
        <SurveyListSidebar />
        <div className="flex-grow-1 page-content-wrap d-flex flex-column">
          <div className="page-heading d-flex align-items-center">
            <div className="flex-grow-1">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/admin/survey/list">{t("survey.title")}</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={`/admin/survey/create/qa-builder?survey_id=${survey_id}`}>{survey_name}</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    <strong>{t("privacyPolicy.heading")}</strong>
                  </li>
                </ol>
              </nav>
              <h1>{t("privacyPolicy.heading")}</h1>
              <p className="text-muted mb-0">{t("privacyPolicy.title")}</p>
            </div>
            {/* <div>
              <Link to="/admin/ducumentation/surveys/document-privacypolicy" className="btn btn-bordered">
                Documentation
              </Link>
            </div> */}
          </div>

          <div className="flex-grow-1">
            <div className="card mb-3">
              <div className="card-body">
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleBold().run()} disabled={!editor || window.modulePermissions.M19.edit != 1}>Bold</button>
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleItalic().run()} disabled={!editor || window.modulePermissions.M19.edit != 1}>Italic</button>
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleUnderline().run()} disabled={!editor || window.modulePermissions.M19.edit != 1}>Underline</button>
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleHeading({ level: 1 }).run()} disabled={!editor || window.modulePermissions.M19.edit != 1}>H1</button>
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleHeading({ level: 2 }).run()} disabled={!editor || window.modulePermissions.M19.edit != 1}>H2</button>
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleHeading({ level: 3 }).run()} disabled={!editor || window.modulePermissions.M19.edit != 1}>H3</button>
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleBulletList().run()} disabled={!editor || window.modulePermissions.M19.edit != 1}>Bulleted List</button>
                  <button className="btn btn-bordered" onClick={() => editor && editor.chain().focus().toggleOrderedList().run()} disabled={!editor || window.modulePermissions.M19.edit != 1}>Numbered List</button>
                  <button className="btn btn-bordered" onClick={setLink} disabled={!editor || window.modulePermissions.M19.edit != 1}>Link</button>
                  <button
                    className="btn btn-bordered"
                    onClick={() => editor && editor.chain().focus().insertTable({ rows: 3, cols: 6, withHeaderRow: true }).run()}
                    disabled={!editor || window.modulePermissions.M19.edit != 1}
                  >
                    Table
                  </button>

                  {/* Text Color Picker */}
                  <div style={{ position: 'relative' }}>
                    <button
                      className="btn btn-bordered"
                      disabled={!editor || window.modulePermissions.M19.edit != 1}
                      onClick={() => colorInputRef.current && colorInputRef.current.click()}
                      title="Text Color"
                    >
                      <span style={{ borderBottom: `3px solid ${currentColor}`, paddingBottom: 1 }}>A</span> Color
                    </button>
                    <input
                      ref={colorInputRef}
                      type="color"
                      value={currentColor}
                      style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                      onChange={(e) => {
                        const color = e.target.value;
                        setCurrentColor(color);
                        if (editor) editor.chain().focus().setColor(color).run();
                      }}
                      disabled={window.modulePermissions.M19.edit != 1}
                    />
                  </div>

                  {/* Unset Color */}
                  <button
                    className="btn btn-bordered"
                    disabled={!editor || window.modulePermissions.M19.edit != 1}
                    onClick={() => {
                      if (editor) {
                        editor.chain().focus().unsetColor().run();
                        setCurrentColor('#000000');
                      }
                    }}
                    title="Remove text color"
                  >
                    No Color
                  </button>
                </div>
                <div className="editor-wrap privacy-editor">
                  <EditorContent editor={editor} />
                </div>
                {/* Local editor table styles for better visibility and to store when copied */}
                <style>{`
                  .privacy-editor .ProseMirror table { width: 100%; border-collapse: collapse; }
                  .privacy-editor .ProseMirror th, .privacy-editor .ProseMirror td { border: 1px solid #000; padding: 8px; }
                  .privacy-editor .ProseMirror thead th { background: #f2f2f2; font-weight: 600; }
                `}</style>
              </div>
            </div>
            <div className="button-wrap d-flex justify-content-end">
              {window.modulePermissions.M19.edit == 1 && (<button className="btn btn-primary" onClick={onSave} disabled={saving || privacyPolicyLoading || !editor}>{(saving || privacyPolicyLoading) ? 'Saving...' : 'Save'}</button>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


